import React, { useState } from "react";
import { StyleSheet, View, ActivityIndicator } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RouteProp } from "@react-navigation/native";
import { MotiView } from "moti";

import { useAppConfig } from "../../AppConfigProvider";
import { useGetStudentDetailedAnalyticsQuery } from "../../graphql/generated/graphql";
import type { RootStackParamList } from "../../navigation/AppNavigator.web";
import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import {
  StudentProfileHeader,
  AcademicOverviewTab,
  SubmissionsAndGradesTab,
  InternalNotesTab,
} from "../../components/adminDashboard/StudentProfile";
import CourseTabNavigator from "../../components/adminDashboard/CourseTabNavigator/CourseTabNavigator";

const StudentProfileTabsTypes = [
  { key: "academic", title: "academic_overview" },
  { key: "submissions", title: "submissions_grades" },
  { key: "notes", title: "internal_notes" },
];

const StudentProfileView: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Student Profile">>();
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];
  const studentId = route.params?.studentId;

  const [activeTab, setActiveTab] = useState<(typeof StudentProfileTabsTypes)[number]["key"]>("academic");

  // GraphQL query for detailed student analytics
  const {
    data: studentData,
    loading: analyticsLoading,
    error: analyticsError,
  } = useGetStudentDetailedAnalyticsQuery({
    variables: {
      schoolId,
      studentId,
    },
    skip: !schoolId || !studentId,
  });

  const handleBackPress = () => {
    navigation.goBack();
  };

  // Transform API data to match our interface
  const transformStudentData = (apiData: any) => {
    if (!apiData?.getStudentDetailedAnalytics?.data?.[0]) {
      return null;
    }

    const student = apiData.getStudentDetailedAnalytics.data[0];

    const transformedStudent = {
      id: student.id,
      label: student.label, // This is the student name
      email: "student@example.com", // Not available in analytics, using placeholder
      profilePicture: "https://via.placeholder.com/80x80",
      program: "N/A", // Not available in analytics query
      // status: status,
      status: student.status,
      enrollmentDate: "N/A", // Not available in analytics query
      gpa: null, // Not available in analytics query
      totalCredits: null, // Not available in analytics query
      completedCourses: student.details?.courses?.filter((c: any) => c.completion_percentage === 100).length || 0,
      coursesInProgress:
        student.details?.courses?.filter((c: any) => c.completion_percentage > 0 && c.completion_percentage < 100)
          .length || 0,
      // Analytics data (this IS the student data)
      success_points: student.success_points,
      confusion_points: student.confusion_points,
      questions_asked: student.questions_asked,
      active_interaction_count: student.active_interaction_count,
      distractions: student.distractions,
      sentiment: student.sentiment,
      total_time_taken: student.total_time_taken,
      completion_percentage: student.completion_percentage,
      details: student.details,
    };

    return transformedStudent;
  };

  const student = transformStudentData(studentData);
  const loading = analyticsLoading;
  const error = analyticsError;

  if (loading) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  if (error || !student) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.errorContainer}>
            <CustomText text="error_loading_student_profile" size="l" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case "academic":
        return <AcademicOverviewTab student={student} integration_id={schoolId} studentId={studentId} />;
      case "submissions":
        return <SubmissionsAndGradesTab student={student} integration_id={schoolId} />;
      case "notes":
        return <InternalNotesTab student={student} />;
      default:
        return <AcademicOverviewTab student={student} integration_id={schoolId} studentId={studentId} />;
    }
  };

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{ opacity: 0, translateY: 20, scale: 1 }}
            animate={{ opacity: 1, translateY: 0, scale: 1 }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            <StudentProfileHeader student={student} onBackPress={handleBackPress} />

            <View style={styles.studentProfileTabs}>
              <CourseTabNavigator
                tabData={StudentProfileTabsTypes}
                activeTab={activeTab}
                onTabPress={(tabKey: string) => setActiveTab(tabKey as (typeof StudentProfileTabsTypes)[number]["key"])}
              />

              <View style={styles.contentArea}>{renderContent()}</View>
            </View>
          </MotiView>
        </View>
      </View>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
    // @ts-expect-error - works on web
    minHeight: "100vh",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  contentArea: {
    flex: 1,
    padding: 32,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  studentProfileTabs: {
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 16,
    // boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  },
});

export default StudentProfileView;
